/**
 * useStreamingDevices -- React hook for fetching all WSD-tracked devices.
 *
 * Endpoint: GET /api/streaming/devices
 *
 * Returns the full device list across all configured servers (Emby/Jellyfin).
 * Also provides mutation hooks for rename and delete operations.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Types (mirrored from backend ExtendedDeviceInfo / toDeviceInfo)
// ============================================================================

export interface StreamingDevice {
  serverType: string;
  username: string;
  deviceId: string;
  deviceName: string;
  customName: string | null;
  platform: string | null;
  clientVersion: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  isActive: boolean;
  isLocked?: boolean;
  firstSeen: number;
  lastSeen: number;
  connectionCount: number;
}

// ============================================================================
// Cache
// ============================================================================

interface CachedDevices {
  devices: StreamingDevice[];
  fetchedAt: number;
}

const devicesCache = new Map<string, CachedDevices>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const CACHE_KEY = "streaming-devices-all";

// ============================================================================
// useStreamingDevices
// ============================================================================

export function useStreamingDevices(enabled: boolean = false): {
  devices: StreamingDevice[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [devices, setDevices] = useState<StreamingDevice[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchDevices = useCallback(
    async (force?: boolean) => {
      if (!enabled) return;

      // Check cache (unless forced)
      if (!force) {
        const cached = devicesCache.get(CACHE_KEY);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setDevices(cached.devices);
          setError(null);
          fetchedKeyRef.current = CACHE_KEY;
          return;
        }
      }

      // Skip if already fetching
      if (isFetchingRef.current && fetchedKeyRef.current === CACHE_KEY) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/streaming/devices", { signal: controller.signal });
        const data = await res.json();

        if (!res.ok || !data.ok || !data.devices) {
          throw new Error(data.message ?? "Failed to load devices");
        }

        const fetchedDevices = data.devices as StreamingDevice[];
        setDevices(fetchedDevices);
        setError(null);

        // Cache
        devicesCache.set(CACHE_KEY, {
          devices: fetchedDevices,
          fetchedAt: Date.now(),
        });
        fetchedKeyRef.current = CACHE_KEY;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = err instanceof Error ? err.message : "Failed to load devices";
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [enabled]
  );

  // Fetch on mount or when enabled changes
  useEffect(() => {
    if (!enabled) {
      setDevices(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    if (fetchedKeyRef.current !== CACHE_KEY) {
      void fetchDevices();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, fetchDevices]);

  const refetch = useCallback(() => {
    // Invalidate cache on manual refetch
    devicesCache.delete(CACHE_KEY);
    void fetchDevices(true);
  }, [fetchDevices]);

  return { devices, isLoading, error, refetch };
}

// ============================================================================
// useDeviceRename
// ============================================================================

export function useDeviceRename(): {
  renaming: boolean;
  rename: (
    serverType: string,
    username: string,
    deviceId: string,
    customName: string | null,
    syncUpstream?: boolean,
    deviceName?: string
  ) => Promise<{ device: StreamingDevice; upstreamSynced: boolean; upstreamError?: string } | null>;
} {
  const [renaming, setRenaming] = useState(false);

  const rename = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string,
      customName: string | null,
      syncUpstream: boolean = true,
      deviceName?: string
    ): Promise<{ device: StreamingDevice; upstreamSynced: boolean; upstreamError?: string } | null> => {
      setRenaming(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}/rename`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({
            customName,
            syncUpstream,
            ...(deviceName ? { deviceName } : {}),
          }),
        });

        const json = await res.json();

        if (!res.ok || !json.ok || !json.device) {
          throw new Error(json.message ?? "Failed to rename device");
        }

        // Invalidate cache
        devicesCache.delete(CACHE_KEY);

        return {
          device: json.device as StreamingDevice,
          upstreamSynced: json.upstreamSynced ?? false,
          upstreamError: json.upstreamError,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to rename device";
        throw new Error(message);
      } finally {
        setRenaming(false);
      }
    },
    []
  );

  return { renaming, rename };
}

// ============================================================================
// useDeviceDelete
// ============================================================================

export function useDeviceDelete(): {
  deleting: boolean;
  deleteDevice: (
    serverType: string,
    username: string,
    deviceId: string
  ) => Promise<boolean>;
} {
  const [deleting, setDeleting] = useState(false);

  const deleteDevice = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string
    ): Promise<boolean> => {
      setDeleting(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}`;

        const res = await fetch(url, {
          method: "DELETE",
          headers: {
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          throw new Error(json.message ?? "Failed to delete device");
        }

        // Invalidate cache
        devicesCache.delete(CACHE_KEY);

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to delete device";
        throw new Error(message);
      } finally {
        setDeleting(false);
      }
    },
    []
  );

  return { deleting, deleteDevice };
}

// ============================================================================
// useDeviceLock
// ============================================================================

export function useDeviceLock(): {
  locking: boolean;
  lockDevice: (
    serverType: string,
    username: string,
    deviceId: string,
    locked: boolean
  ) => Promise<boolean>;
} {
  const [locking, setLocking] = useState(false);

  const lockDevice = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string,
      locked: boolean
    ): Promise<boolean> => {
      setLocking(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}/lock`;

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ locked }),
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          throw new Error(json.message ?? "Failed to update device lock");
        }

        // Invalidate cache
        devicesCache.delete(CACHE_KEY);

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update device lock";
        throw new Error(message);
      } finally {
        setLocking(false);
      }
    },
    []
  );

  return { locking, lockDevice };
}

// ============================================================================
// useDeviceNameSync
// ============================================================================

export interface DeviceSyncResult {
  deviceId: string;
  serverType: string;
  username: string;
  deviceName: string;
  success: boolean;
  error?: string;
}

export interface DeviceSyncResponse {
  total: number;
  synced: number;
  failed: number;
  skipped: number;
  results: DeviceSyncResult[];
}

export function useDeviceNameSync(): {
  syncing: boolean;
  syncNames: () => Promise<DeviceSyncResponse | null>;
} {
  const [syncing, setSyncing] = useState(false);

  const syncNames = useCallback(async (): Promise<DeviceSyncResponse | null> => {
    setSyncing(true);
    try {
      const csrfToken = getCsrfToken();
      const res = await fetch("/api/streaming/devices/sync-names", {
        method: "POST",
        headers: {
          ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        },
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.message ?? "Failed to sync device names");
      }

      // Invalidate cache so next fetch reflects any upstream changes
      devicesCache.delete(CACHE_KEY);

      return {
        total: json.total as number,
        synced: json.synced as number,
        failed: json.failed as number,
        skipped: json.skipped as number,
        results: json.results as DeviceSyncResult[],
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to sync device names";
      throw new Error(message);
    } finally {
      setSyncing(false);
    }
  }, []);

  return { syncing, syncNames };
}
